import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../features/auth/context';
import { useToast } from '../hooks/useToast';
import { useNavigate } from 'react-router-dom';
import {
  sendAdminTestEmail, getAdminEmailLogs, AdminEmailLog,
  listCampaigns, getCampaignStatus, sendCampaignTest, startCampaign,
  AdminCampaignInfo, AdminCampaignStatus, ExtraRecipient,
} from '../features/admin/api';
import Table from '../components/ui/table';
import type { Column } from '../components/ui/table';
import Button from '../components/ui/button';
import AdminCsvExportButton from '../features/admin/AdminCsvExportButton';
import Input from '../components/ui/Input';
import ConfirmationDialog from '../components/ConfirmationDialog';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// One recipient per line: "email" or "email, FirstName".
const parseExtraRecipients = (text: string): { recipients: ExtraRecipient[]; invalid: string[] } => {
  const recipients: ExtraRecipient[] = [];
  const invalid: string[] = [];
  text.split('\n').map((l) => l.trim()).filter(Boolean).forEach((line) => {
    const [email, ...rest] = line.split(',').map((p) => p.trim());
    if (EMAIL_REGEX.test(email)) recipients.push({ email, firstName: rest.join(' ') || null });
    else invalid.push(line);
  });
  return { recipients, invalid };
};

// One card per campaign in the backend registry (services/campaigns.js).
// Same three steps everywhere: refresh + eyeball the audience, test send,
// confirm-and-send. Campaigns with their own audience() ignore pasted
// extras, so the extras box only shows for registry entries without one.
const CampaignCard: React.FC<{
  info: AdminCampaignInfo;
  defaultTestTo: string;
  onLogsRefresh: () => void;
}> = ({ info, defaultTestTo, onLogsRefresh }) => {
  const { showToast } = useToast();

  const [campaign, setCampaign] = useState<AdminCampaignStatus | null>(null);
  const [extrasText, setExtrasText] = useState('');
  const [campaignBusy, setCampaignBusy] = useState(false);
  const [confirmingSend, setConfirmingSend] = useState(false);
  const [campaignTestTo, setCampaignTestTo] = useState(defaultTestTo);
  const [refreshingAudience, setRefreshingAudience] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    setCampaignTestTo((prev) => prev || defaultTestTo);
  }, [defaultTestTo]);

  const refreshCampaign = useCallback(async (extras: ExtraRecipient[]) => {
    try {
      const status = await getCampaignStatus(info.key, extras);
      setCampaign(status);
      return status;
    } catch (e: any) {
      const msg = e?.response?.data?.error || e.message || 'Failed to load campaign status.';
      showToast({ title: 'Error', message: msg, type: 'error' });
      return null;
    }
  }, [info.key, showToast]);

  useEffect(() => { refreshCampaign([]); }, [refreshCampaign]);

  // While a send is running, poll so the counts and the log fill in live.
  useEffect(() => {
    if (!campaign?.running) return;
    const t = setInterval(() => {
      refreshCampaign(parseExtraRecipients(extrasText).recipients);
      onLogsRefresh();
    }, 5000);
    return () => clearInterval(t);
  }, [campaign?.running, extrasText, refreshCampaign, onLogsRefresh]);

  const refreshAudienceFromExtras = useCallback(async ({ quiet = false } = {}) => {
    const { recipients, invalid } = parseExtraRecipients(extrasText);
    if (invalid.length) {
      showToast({ title: 'Some lines ignored', message: `Not valid emails: ${invalid.slice(0, 3).join(' · ')}${invalid.length > 3 ? ` (+${invalid.length - 3} more)` : ''}`, type: 'warning' });
    }
    setRefreshingAudience(true);
    const status = await refreshCampaign(recipients);
    setRefreshingAudience(false);
    if (status && !quiet) {
      showToast({
        title: 'Audience updated',
        message: `${status.audience.total} people${info.hasCustomAudience ? '' : ` (${status.audience.extra} from your pasted list)`} · ${status.remaining} still to send.`,
        type: 'success'
      });
    }
    return status;
  }, [extrasText, info.hasCustomAudience, refreshCampaign, showToast]);

  // Refresh with the current extras BEFORE confirming, so the number in the
  // dialog is never stale. Quiet: the dialog itself is the feedback.
  const openSendConfirmation = useCallback(async () => {
    const status = await refreshAudienceFromExtras({ quiet: true });
    if (!status) return;
    if (status.remaining === 0) {
      showToast({ title: 'Nothing to send', message: 'Everyone in the audience has already received this campaign.', type: 'info' });
      return;
    }
    setConfirmingSend(true);
  }, [refreshAudienceFromExtras, showToast]);

  const handleCampaignTest = useCallback(async () => {
    const to = campaignTestTo.trim();
    if (!EMAIL_REGEX.test(to)) {
      showToast({ title: 'Invalid email', message: `"${to}" is not a valid email address.`, type: 'error' });
      return;
    }
    try {
      setCampaignBusy(true);
      const result = await sendCampaignTest(info.key, to);
      showToast({ title: 'Campaign test sent', message: `Sent to ${result.to}. Check how it renders.`, type: 'success' });
    } catch (e: any) {
      const msg = e?.response?.data?.details || e?.response?.data?.error || e.message || 'Failed to send campaign test.';
      showToast({ title: 'Campaign test failed', message: msg, type: 'error' });
    } finally {
      setCampaignBusy(false);
      onLogsRefresh();
    }
  }, [campaignTestTo, info.key, showToast, onLogsRefresh]);

  const handleStartCampaign = useCallback(async () => {
    setConfirmingSend(false);
    try {
      setCampaignBusy(true);
      const { recipients } = parseExtraRecipients(extrasText);
      const result = await startCampaign(info.key, recipients);
      showToast({ title: 'Campaign started', message: `Sending to ${result.remaining} recipients (~1/sec). Progress appears below.`, type: 'success' });
      setCampaign({ ...result, running: true });
    } catch (e: any) {
      const msg = e?.response?.data?.error || e.message || 'Failed to start campaign.';
      showToast({ title: 'Campaign not started', message: msg, type: 'error' });
    } finally {
      setCampaignBusy(false);
    }
  }, [extrasText, info.key, showToast]);

  return (
    <div className="mb-6 p-4 rounded-lg border border-emerald-200 bg-emerald-50/40">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h2 className="text-sm font-semibold">
          <code className="text-xs text-slate-500 mr-2">{info.key}</code>
          “{campaign?.subject || info.subject}”
          {info.createdAt && (
            <span className="ml-2 text-xs font-normal text-slate-500 whitespace-nowrap">created {info.createdAt}</span>
          )}
        </h2>
        {campaign && (
          <span className="text-xs text-slate-600">
            Audience {campaign.audience.total} ({campaign.audience.users} users · {campaign.audience.waitlist} waitlist · {campaign.audience.extra} extra)
            {' · '}already sent {campaign.alreadySent} · unsubscribed {campaign.suppressed} ·{' '}
            <strong>remaining {campaign.remaining}</strong>
            {campaign.running && <span className="text-emerald-700 font-semibold"> · sending…</span>}
          </span>
        )}
      </div>

      {!info.hasCustomAudience && (
        <>
          <label className="block text-xs text-slate-600 mb-1" htmlFor={`extra-recipients-${info.key}`}>
            Extra recipients (from your spreadsheet), one per line: <code>email, FirstName</code>
          </label>
          <textarea
            id={`extra-recipients-${info.key}`}
            value={extrasText}
            onChange={(e) => setExtrasText(e.target.value)}
            rows={4}
            placeholder={'maria@example.com, Maria\njoao@example.com'}
            className="w-full text-sm font-mono rounded-md border border-slate-300 p-2 mb-3"
          />
        </>
      )}

      {/* Step 1: check the audience — counts AND the exact recipient list. */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <Button variant="secondary" onClick={() => refreshAudienceFromExtras()} disabled={refreshingAudience || campaignBusy}>
          {refreshingAudience ? 'Refreshing…' : 'Refresh audience'}
        </Button>
        <Button
          variant="secondary"
          onClick={() => setShowPreview((v) => !v)}
          disabled={!campaign || campaign.preview.length === 0}
        >
          {showPreview ? 'Hide recipients' : `Show recipients (${campaign?.preview.length ?? '…'})`}
        </Button>
        <span className="text-xs text-slate-500">Read the list before sending — a send cannot be undone.</span>
      </div>

      {showPreview && campaign && campaign.preview.length > 0 && (
        <div className="mb-3 max-h-48 overflow-y-auto rounded-md border border-emerald-100 bg-white p-2">
          <ul className="text-xs font-mono text-slate-700">
            {campaign.preview.map((r) => (
              <li key={r.email} className="py-0.5">
                {r.email}{r.firstName ? <span className="text-slate-400"> · {r.firstName}</span> : null}
              </li>
            ))}
          </ul>
          {campaign.remaining > campaign.preview.length && (
            <div className="text-xs text-slate-500 mt-1">
              …and {campaign.remaining - campaign.preview.length} more (preview capped at 200).
            </div>
          )}
        </div>
      )}

      <div className="h-px bg-emerald-100 mb-3" />

      {/* Step 2: preview the email */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <label className="text-sm text-slate-700" htmlFor={`campaign-test-to-${info.key}`}>Send a test to</label>
        <Input
          id={`campaign-test-to-${info.key}`}
          type="email"
          value={campaignTestTo}
          onChange={(e) => setCampaignTestTo(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !campaignBusy) handleCampaignTest(); }}
          className="w-72"
          aria-label={`Campaign test recipient for ${info.key}`}
        />
        <Button variant="secondary" onClick={handleCampaignTest} disabled={campaignBusy || campaign?.running}>
          Send test
        </Button>
      </div>

      <div className="h-px bg-emerald-100 mb-3" />

      {/* Step 3: the real send */}
      <div className="flex items-center gap-3 flex-wrap">
        <Button
          onClick={openSendConfirmation}
          disabled={campaignBusy || refreshingAudience || !campaign || campaign.running}
        >
          {campaign?.running ? 'Sending…' : `Send to ${campaign?.remaining ?? '…'} recipients`}
        </Button>
        <span className="text-xs text-slate-500">
          Asks for confirmation first. Safe to re-run: anyone already sent to is skipped.
        </span>
      </div>

      {confirmingSend && campaign && (
        <ConfirmationDialog
          title={`Send "${info.key}"?`}
          message={`This will email ${campaign.remaining} people, about one per second. Ready?`}
          onConfirm={handleStartCampaign}
          onCancel={() => setConfirmingSend(false)}
        />
      )}
    </div>
  );
};

// Admin subpage: send a test email, drive campaigns, audit the outbound-email
// log. Backend: routes/admin.js (email-logs, test-email, campaigns/*).
// Data source: email_logs table — see docs/integrations/aws-ses.md.
const AdminEmailsPage: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [logs, setLogs] = useState<AdminEmailLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendingTest, setSendingTest] = useState(false);
  const [testRecipient, setTestRecipient] = useState('');
  const [campaigns, setCampaigns] = useState<AdminCampaignInfo[]>([]);

  useEffect(() => {
    if (!authLoading && (!user || !user.isAdmin)) {
      showToast({ title: 'Access Denied', message: 'Admin access required. Redirecting to homepage.', type: 'error' });
      navigate('/');
    }
  }, [authLoading, user, navigate, showToast]);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAdminEmailLogs();
      setLogs(data);
    } catch (e: any) {
      const msg = e?.response?.data?.error || e.message || 'Failed to load email logs.';
      showToast({ title: 'Error', message: msg, type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const fetchCampaigns = useCallback(async () => {
    try {
      const data = await listCampaigns();
      setCampaigns(data);
    } catch (e: any) {
      const msg = e?.response?.data?.error || e.message || 'Failed to load campaigns.';
      showToast({ title: 'Error', message: msg, type: 'error' });
    }
  }, [showToast]);

  useEffect(() => {
    if (user && !authLoading && user.isAdmin) {
      fetchLogs();
      fetchCampaigns();
    }
  }, [user, authLoading, fetchLogs, fetchCampaigns]);

  const handleSendTestEmail = useCallback(async () => {
    const to = (testRecipient.trim() || user?.email || '').trim();
    if (!to) return;
    if (!EMAIL_REGEX.test(to)) {
      showToast({ title: 'Invalid email', message: `"${to}" is not a valid email address.`, type: 'error' });
      return;
    }
    try {
      setSendingTest(true);
      const result = await sendAdminTestEmail(to);
      showToast({ title: 'Test email sent', message: `Sent to ${to}. MessageId: ${result.messageId}`, type: 'success' });
    } catch (e: any) {
      const msg = e?.response?.data?.details || e?.response?.data?.error || e.message || 'Failed to send test email.';
      showToast({ title: 'Failed to send test email', message: msg, type: 'error' });
    } finally {
      setSendingTest(false);
      // Both successes and failures are logged server-side — refresh either way.
      fetchLogs();
    }
  }, [testRecipient, user, showToast, fetchLogs]);

  const formatDateTime = (s: string) =>
    new Date(s).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  const sentCount = useMemo(() => logs.filter((l) => l.status === 'sent').length, [logs]);
  const failedCount = useMemo(() => logs.filter((l) => l.status === 'failed').length, [logs]);

  const columns: Column<AdminEmailLog>[] = [
    {
      key: 'createdAt',
      header: 'Sent At',
      className: 'min-w-[12ch]',
      render: (r) => <span className="whitespace-nowrap text-sm">{formatDateTime(r.createdAt)}</span>,
    },
    {
      key: 'category',
      header: 'Category',
      className: 'min-w-[12ch]',
      render: (r) => (
        <span className="px-2 py-1 rounded-full text-xs bg-emerald-50 text-emerald-700 border border-emerald-200">
          {r.category}
        </span>
      ),
    },
    {
      key: 'recipient',
      header: 'Recipient',
      className: 'min-w-[18ch]',
      render: (r) => <span className="text-sm font-mono">{r.recipient}</span>,
    },
    {
      key: 'subject',
      header: 'Subject',
      className: 'min-w-[20ch]',
      render: (r) => <span className="text-sm">{r.subject}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      className: 'min-w-[8ch]',
      render: (r) =>
        r.status === 'sent' ? (
          <span className="text-emerald-700 text-xs font-medium">sent</span>
        ) : r.status === 'suppressed' ? (
          <span className="text-slate-500 text-xs font-medium">suppressed (unsubscribed)</span>
        ) : (
          <span className="text-red-700 text-xs font-medium" title={r.errorType || undefined}>
            failed{r.errorType ? ` (${r.errorType})` : ''}
          </span>
        ),
    },
  ];

  if (authLoading || !user || !user.isAdmin) return null;

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <button onClick={() => navigate('/admin')} className="text-sm text-emerald-700 hover:underline mb-1">
            ← Admin Panel
          </button>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Emails</h1>
        </div>
        <div className="text-sm text-slate-600 text-right">
          <div>Showing: {logs.length}</div>
          <div>{sentCount} sent · {failedCount} failed</div>
        </div>
      </div>

      {/* Send a test email */}
      <div className="mb-6 p-4 rounded-lg border border-slate-200 bg-slate-50">
        <h2 className="text-sm font-semibold mb-2">Send a test email</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            type="email"
            value={testRecipient}
            onChange={(e) => setTestRecipient(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !sendingTest) handleSendTestEmail(); }}
            placeholder={user?.email || 'recipient@example.com'}
            className="w-72"
            aria-label="Test email recipient"
          />
          <Button variant="secondary" onClick={handleSendTestEmail} disabled={sendingTest}>
            {sendingTest ? 'Sending…' : 'Send Test Email'}
          </Button>
          <span className="text-xs text-slate-500">Leave blank to send to yourself ({user?.email}).</span>
        </div>
      </div>

      {/* One card per campaign in the backend registry */}
      {campaigns.map((info) => (
        <CampaignCard key={info.key} info={info} defaultTestTo={user?.email || ''} onLogsRefresh={fetchLogs} />
      ))}

      <div className="flex items-center justify-between gap-2 mb-3">
        <h2 className="text-lg font-semibold">Recent emails</h2>
        <AdminCsvExportButton table="email-logs" label="emails" />
      </div>
      {loading ? (
        <div className="text-slate-600">Loading…</div>
      ) : (
        <div className="table-compact">
          <Table columns={columns} data={logs} emptyMessage="No emails sent yet." />
        </div>
      )}
    </div>
  );
};

export default AdminEmailsPage;
